// Messaging Bot Service
//
// Handles outbound bot actions: daily summaries, anomaly alerts, and query responses.
// Relies on the user keeping VeriGit open (or a Service Worker active).
// All message formatting is done client-side; no hosted backend required.

import { slackService } from './slack-service';
import { teamsService } from './teams-service';
import {
  getSlackSettings,
  getTeamsSettings,
  getPendingBotActions,
  enqueueBotAction,
  markBotActionSent,
  markBotActionFailed,
} from './messaging-storage';
import type {
  MessagingSnapshot,
  MessagingAnomaly,
  BotAction,
  MessagingPlatform,
} from './messaging-types';

// ─── ID helpers ───────────────────────────────────────────────────────────────

function nanoid(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ─── Slack formatting (mrkdwn) ────────────────────────────────────────────────

function formatSlackDailySummary(snapshot: MessagingSnapshot): string {
  const date = new Date(snapshot.date).toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const topChannels = [...snapshot.channelStats]
    .sort((a, b) => b.messageCount - a.messageCount)
    .slice(0, 5);

  const topMembers = [...snapshot.memberStats]
    .sort((a, b) => b.messageCount - a.messageCount)
    .slice(0, 5);

  const lines: string[] = [
    `*:bar_chart: VeriGit Daily Slack Summary — ${date}*`,
    '',
    `• *${snapshot.totalMessages}* messages across *${snapshot.activeChannels}* channels`,
    `• *${snapshot.activeMembers}* active members`,
    '',
    '*Top channels:*',
    ...topChannels.map(c => `  › #${c.channelName}: ${c.messageCount} msgs from ${c.uniqueAuthors} members`),
    '',
    '*Most active members:*',
    ...topMembers.map(m => `  › ${m.memberName}: ${m.messageCount} messages`),
  ];

  if (snapshot.anomalies.length > 0) {
    lines.push('', '*:warning: Anomalies detected:*');
    for (const a of snapshot.anomalies) {
      const emoji = a.severity === 'high' ? ':red_circle:' : a.severity === 'medium' ? ':large_yellow_circle:' : ':white_circle:';
      lines.push(`  ${emoji} ${a.description}`);
    }
  }

  lines.push('', '_Powered by VeriGit — client-side analytics_');
  return lines.join('\n');
}

function formatSlackAnomalyAlert(anomaly: MessagingAnomaly): string {
  const severityEmoji = anomaly.severity === 'high' ? ':red_circle:' : anomaly.severity === 'medium' ? ':large_yellow_circle:' : ':white_circle:';
  return [
    `${severityEmoji} *VeriGit Anomaly Alert*`,
    `> ${anomaly.description}`,
    `_Detected at ${new Date(anomaly.detectedAt).toLocaleTimeString('en-AU')}_`,
  ].join('\n');
}

// ─── Teams formatting (HTML) ──────────────────────────────────────────────────

function formatTeamsDailySummary(snapshot: MessagingSnapshot): string {
  const date = new Date(snapshot.date).toLocaleDateString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  const topChannels = [...snapshot.channelStats]
    .sort((a, b) => b.messageCount - a.messageCount)
    .slice(0, 5);

  const topMembers = [...snapshot.memberStats]
    .sort((a, b) => b.messageCount - a.messageCount)
    .slice(0, 5);

  const anomalyHtml = snapshot.anomalies.length > 0
    ? `<p><b>⚠️ Anomalies detected:</b></p><ul>${snapshot.anomalies.map(a =>
        `<li>${a.severity === 'high' ? '🔴' : a.severity === 'medium' ? '🟡' : '⚪'} ${a.description}</li>`
      ).join('')}</ul>`
    : '';

  return [
    `<h3>📊 VeriGit Daily Teams Summary — ${date}</h3>`,
    `<p><b>${snapshot.totalMessages}</b> messages across <b>${snapshot.activeChannels}</b> channels · <b>${snapshot.activeMembers}</b> active members</p>`,
    '<p><b>Top channels:</b></p>',
    `<ul>${topChannels.map(c => `<li><b>${c.channelName}</b>: ${c.messageCount} msgs from ${c.uniqueAuthors} members</li>`).join('')}</ul>`,
    '<p><b>Most active members:</b></p>',
    `<ul>${topMembers.map(m => `<li><b>${m.memberName}</b>: ${m.messageCount} messages</li>`).join('')}</ul>`,
    anomalyHtml,
    '<p><em>Powered by VeriGit — client-side analytics</em></p>',
  ].join('');
}

function formatTeamsAnomalyAlert(anomaly: MessagingAnomaly): string {
  const icon = anomaly.severity === 'high' ? '🔴' : anomaly.severity === 'medium' ? '🟡' : '⚪';
  return `<p>${icon} <b>VeriGit Anomaly Alert</b></p><blockquote>${anomaly.description}</blockquote><p><em>Detected at ${new Date(anomaly.detectedAt).toLocaleTimeString('en-AU')}</em></p>`;
}

// ─── BotService ───────────────────────────────────────────────────────────────

export class BotService {
  private static instance: BotService;

  private constructor() {}

  static getInstance(): BotService {
    if (!BotService.instance) BotService.instance = new BotService();
    return BotService.instance;
  }

  // ── Queue outbound actions ────────────────────────────────────────────────

  async queueDailySummary(
    platform: MessagingPlatform,
    snapshot: MessagingSnapshot,
    targetChannelId: string,
    targetTeamId?: string,
  ): Promise<BotAction> {
    const payload = platform === 'slack'
      ? formatSlackDailySummary(snapshot)
      : formatTeamsDailySummary(snapshot);

    const action: BotAction = {
      id: nanoid(),
      type: 'daily_summary',
      platform,
      targetChannelId,
      targetTeamId,
      payload,
      queuedAt: new Date().toISOString(),
    };
    await enqueueBotAction(action);
    return action;
  }

  async queueAnomalyAlert(
    platform: MessagingPlatform,
    anomaly: MessagingAnomaly,
    targetChannelId: string,
    targetTeamId?: string,
  ): Promise<BotAction> {
    const payload = platform === 'slack'
      ? formatSlackAnomalyAlert(anomaly)
      : formatTeamsAnomalyAlert(anomaly);

    const action: BotAction = {
      id: nanoid(),
      type: 'anomaly_alert',
      platform,
      targetChannelId,
      targetTeamId,
      payload,
      queuedAt: new Date().toISOString(),
    };
    await enqueueBotAction(action);
    return action;
  }

  /**
   * Queue a simple text response (e.g. to a query mention).
   */
  async queueQueryResponse(
    platform: MessagingPlatform,
    text: string,
    targetChannelId: string,
    targetTeamId?: string,
  ): Promise<BotAction> {
    const action: BotAction = {
      id: nanoid(),
      type: 'query_response',
      platform,
      targetChannelId,
      targetTeamId,
      payload: text,
      queuedAt: new Date().toISOString(),
    };
    await enqueueBotAction(action);
    return action;
  }

  // ── Flush pending queue ───────────────────────────────────────────────────

  /**
   * Attempt to send all pending bot actions for a platform.
   * Should be called after a successful poll cycle or on app focus.
   */
  async flushQueue(platform: MessagingPlatform): Promise<void> {
    const pending = await getPendingBotActions(platform);
    if (pending.length === 0) return;

    if (platform === 'slack') {
      const settings = await getSlackSettings();
      if (!settings?.enabled || !settings.accessToken) return;

      for (const action of pending) {
        try {
          await slackService.postMessage(settings.accessToken, action.targetChannelId, action.payload);
          await markBotActionSent(action.id);
        } catch (err) {
          await markBotActionFailed(action.id, String(err));
          console.warn(`[BotService] Slack action ${action.id} failed:`, err);
        }
      }
    }

    if (platform === 'teams') {
      const settings = await getTeamsSettings();
      if (!settings?.enabled || !settings.accessToken) return;

      for (const action of pending) {
        if (!action.targetTeamId) continue;
        try {
          await teamsService.postHtml(
            settings.accessToken,
            action.targetTeamId,
            action.targetChannelId,
            action.payload,
          );
          await markBotActionSent(action.id);
        } catch (err) {
          await markBotActionFailed(action.id, String(err));
          console.warn(`[BotService] Teams action ${action.id} failed:`, err);
        }
      }
    }
  }

  // ── Query detection ───────────────────────────────────────────────────────

  /**
   * Check whether an incoming message is a bot mention/query and return a
   * plain-text response if it matches known commands, or null otherwise.
   *
   * Recognised commands (case-insensitive):
   *   @verigit status        → overall team health one-liner
   *   @verigit summary       → today's snapshot summary
   *   @verigit quiet         → list quiet members
   *   @verigit anomalies     → list current anomalies
   */
  buildQueryResponse(
    text: string,
    snapshot: MessagingSnapshot | null,
  ): string | null {
    const lower = text.toLowerCase();
    if (!lower.includes('@verigit')) return null;

    if (lower.includes('status')) {
      if (!snapshot) return 'No data yet — VeriGit is still warming up.';
      return `📊 Today: ${snapshot.totalMessages} messages, ${snapshot.activeMembers} active members, ${snapshot.anomalies.length} anomaly${snapshot.anomalies.length !== 1 ? 'ies' : ''} detected.`;
    }

    if (lower.includes('summary')) {
      if (!snapshot) return 'No data yet — check back after the next poll.';
      const top = [...snapshot.channelStats].sort((a, b) => b.messageCount - a.messageCount)[0];
      return `📋 Summary for ${snapshot.date}: ${snapshot.totalMessages} total messages across ${snapshot.activeChannels} channels. Most active: #${top?.channelName ?? 'n/a'} (${top?.messageCount ?? 0} msgs).`;
    }

    if (lower.includes('quiet')) {
      if (!snapshot || snapshot.quietMembers.length === 0) return '✅ No quiet members detected today.';
      return `🤫 Quiet today: ${snapshot.quietMembers.join(', ')}`;
    }

    if (lower.includes('anomal')) {
      if (!snapshot || snapshot.anomalies.length === 0) return '✅ No anomalies detected.';
      return snapshot.anomalies.map(a => `• ${a.description}`).join('\n');
    }

    return `👋 Hi! Try: @verigit status · @verigit summary · @verigit quiet · @verigit anomalies`;
  }
}

export const botService = BotService.getInstance();
